import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Lock, Play, Trophy } from "lucide-react";
import Leaderboard from "@/components/Leaderboard";
import { GAMES } from "@/game/games";

export default function ArcadeLobby() {
  const navigate = useNavigate();
  const [lobby, setLobby] = useState(null);
  const [focus, setFocus] = useState("asteroids");

  useEffect(() => {
    API.get("/arcade/lobby")
      .then(({ data }) => setLobby(data))
      .catch(() => {
        toast.error("Finish your math practice to unlock the arcade!", { id: "no-reward" });
        navigate("/student");
      });
  }, []); // eslint-disable-line

  if (!lobby) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-mono text-cyber animate-pulse tracking-widest">ENTERING ARCADE...</div>
      </div>
    );
  }

  const focusGame = GAMES.find((g) => g.id === focus);

  const skipGame = async () => {
    try {
      await API.post("/arcade/skip");
      toast.success("Game skipped. Back to practice!");
      navigate("/practice");
    } catch {
      toast.error("Couldn't skip the game. Try again.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 pt-24 pb-16" data-testid="arcade-lobby">
      <button onClick={() => navigate("/student")} className="flex items-center gap-2 text-white/40 hover:text-white mb-8 font-body text-sm transition-colors duration-200" data-testid="lobby-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
      </button>
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter mb-2">
        Game <span className="neon-text-cyber">Lobby</span>
      </h1>
      <p className="font-body text-white/40 mb-10" data-testid="lobby-mode">
        Reward unlocked: {lobby.bonus_mode ? <span className="text-cyber font-bold">BONUS MODE (play until game over)</span> : "Standard (60 seconds)"}. Pick a game!
        <button onClick={skipGame} data-testid="lobby-skip-game-btn"
          className="ml-4 font-body text-sm text-white/40 hover:text-white underline underline-offset-4 transition-colors duration-200">
          Skip game and keep practicing
        </button>
      </p>

      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="grid sm:grid-cols-2 gap-6">
          {GAMES.map((g, i) => (
            <Cabinet key={g.id} game={g} index={i} stats={lobby.best[g.id]} focused={focus === g.id}
              onFocus={() => g.status === "live" && setFocus(g.id)}
              onPlay={() => navigate(`/arcade/play/${g.id}`)} />
          ))}
        </div>
        <div>
          <p className="font-mono text-xs text-white/40 uppercase tracking-wider mb-3" data-testid="lobby-leaderboard-title">
            {focusGame?.name} top scores
          </p>
          <Leaderboard game={focus} />
        </div>
      </div>
    </div>
  );
}

function Cabinet({ game, index, stats, focused, onFocus, onPlay }) {
  const live = game.status === "live";
  const Icon = game.icon;
  return (
    <div
      onMouseEnter={onFocus}
      onClick={onFocus}
      data-testid={`cabinet-${game.id}`}
      className={`group relative rounded-2xl overflow-hidden border-2 bg-black/60 animate-slide-up transition-[transform,border-color,box-shadow] duration-300 ${live ? "hover:-translate-y-1.5 cursor-pointer" : "opacity-60"}`}
      style={{
        animationDelay: `${index * 90}ms`,
        borderColor: focused ? game.color : "rgba(255,255,255,0.08)",
        boxShadow: focused ? `0 0 32px ${game.color}55` : "none",
      }}
    >
      <div className="aspect-[3/2] relative overflow-hidden bg-white/[0.03]">
        {game.image ? (
          <img src={game.image} alt={game.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-16 h-16" style={{ color: game.color }} />
          </div>
        )}
        {!live && (
          <span className="absolute top-3 right-3 font-mono text-xs px-2 py-1 rounded-full bg-black/70 text-white/70 flex items-center gap-1" data-testid={`cabinet-${game.id}-soon`}>
            <Lock className="w-3 h-3" /> COMING SOON
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-5 h-5" style={{ color: game.color }} />
          <h2 className="font-heading text-lg font-bold text-white">{game.name}</h2>
        </div>
        <p className="font-body text-sm text-white/50 mb-4 min-h-[40px]">{game.desc}</p>
        {live && (
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 font-mono text-xs text-white/50" data-testid={`cabinet-${game.id}-best`}>
              <Trophy className="w-4 h-4 text-yellow-400" />
              Best: <span className="text-white font-bold">{(stats?.best || 0).toLocaleString()}</span>
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onPlay(); }}
              data-testid={`play-${game.id}-btn`}
              className="btn-cyber px-5 py-2 rounded-full font-heading text-sm tracking-wider flex items-center gap-1.5"
            >
              <Play className="w-4 h-4" /> PLAY
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
